import { Fund, ValueOf } from "creso-core/types";
import {
  createColumnHelper,
  useReactTable,
  getCoreRowModel,
} from "@tanstack/react-table";
import { useMemo } from "react";
import get from "lodash/get";
import {
  cn,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  DataTable,
} from "web-design-system";
import { ChevronDown } from "lucide-react";
import { historicalReturnsRowConfig } from "./config";
import { HistoryReturnsCell } from "./historical-returns-cell";

export function HistoricalReturns({
  funds,
  labelWidth,
  variant = "default",
}: {
  funds: Fund[];
  labelWidth: number;
  variant?: "default" | "report";
}) {
  const columnHelper = createColumnHelper<{
    label: string;
    [key: string]: string;
  }>();

  const columns = useMemo(() => {
    const firstColumn = [
      columnHelper.accessor("label", {
        header: "",
        meta: {
          className: "bg-muted px-5 py-3",
        },
        size: labelWidth,
        maxSize: labelWidth,
      }),
    ];

    return firstColumn
      .concat(
        funds.map((fund, index) => {
          return columnHelper.accessor(fund.fund_id, {
            header: "",
            meta: {
              className: `bg-background text-left px-5 py-3 text-sm ${
                index === funds.length - 1 ? "" : "border-r border-border"
              }`,
            },
            cell: ({ row }) => {
              return (
                <HistoryReturnsCell label={row.original.label} fund={fund} />
              );
            },
            size: Number.MAX_SAFE_INTEGER,
          });
        })
      )
      .concat(
        variant === "default"
          ? [
              columnHelper.accessor("space", {
                header: "",
                size: 80,
                meta: {
                  className: "bg-background px-5 py-3",
                },
              }),
            ]
          : []
      );
  }, [columnHelper, funds, labelWidth, variant]);

  const data = useMemo(() => {
    return historicalReturnsRowConfig.map((config) => {
      const values = funds.reduce(
        (acc, fund) => {
          acc[fund.fund_id] = get(fund, config.accessor);
          return acc;
        },
        {} as Record<Fund["fund_id"], ValueOf<Fund>>
      );
      return {
        label: config.label,
        ...values,
      } as { label: string; [key: Fund["fund_id"]]: string };
    });
  }, [funds]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: false,
  });

  return (
    <div className="flex flex-col break-inside-avoid">
      <Collapsible
        defaultOpen
        className="w-full bg-muted rounded-lg border border-border group"
      >
        <CollapsibleTrigger
          className={cn(
            "flex w-full justify-between items-center px-5 py-4",
            variant === "report" && "pointer-events-none"
          )}
        >
          <span className="font-semibold text-base">Historical Returns</span>
          {variant === "default" ? (
            <ChevronDown className="w-4 h-4 transition-transform duration-300 group-data-[state=open]:rotate-180 text-muted-foreground" />
          ) : null}
        </CollapsibleTrigger>
        <CollapsibleContent>
          <DataTable
            pagination={false}
            table={table}
            noHeader
            className="border-none [&_table]:table-fixed"
          />
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
